import React from 'react';
import { Award, Info, Plus, Save, Shapes, Trash2, Trophy } from 'lucide-react';
import { observer } from 'mobx-react';
import { useWrapRequest } from 'use-wrap-request';
import { Alert, AlertDescription } from '@components/ui/alert';
import { Badge } from '@components/ui/badge';
import { Button } from '@components/ui/button';
import { Label } from '@components/ui/label';
import { Separator } from '@components/ui/separator';
import { Switch } from '@components/ui/switch';
import {
    Tooltip,
    TooltipContent,
    TooltipTrigger
} from '@components/ui/tooltip';
import { cn } from '@core/utils/cn';
import {
    BadgeCategoryAdminDto,
    BadgeTypeAdminDto,
    BadgeLevelAdminDto,
    BadgeKind,
    BadgeStatus,
    BadgeVisibility,
    BadgeOwnerType,
    CdnObjectDto
} from '@api/FitnessActivityClient';
import { BadgeImageUpload } from './badge-image-upload';
import { I18NText } from '@api/ChallengeClient';
import LocalizedTextField from '../components/textfield/localized_textfield';
import LocalizedTextArea from '../components/text-area/localized-text-area';
import { Select } from '../components/select';
import { TextField } from '../components/textfield';
import { Api } from '../core/api';
import { toast } from '../core/toast';
import { injectTSDI } from '../core/tsdi';
import { BadgeNodeType, BadgeTreeNode } from './badge-tree';
import { CommunitySelect } from '@components/community-select';
import { GoalSelect } from '@components/goal-select';

interface BadgeDetailPanelProps {
    selectedNode: BadgeTreeNode | null;
    createMode: { nodeType: BadgeNodeType; parentId?: number } | null;
    onNodeUpdated: (
        nodeId: string,
        data: BadgeCategoryAdminDto | BadgeTypeAdminDto | BadgeLevelAdminDto
    ) => void;
    onNodeCreated: (
        nodeType: BadgeNodeType,
        data: BadgeCategoryAdminDto | BadgeTypeAdminDto | BadgeLevelAdminDto,
        parentId?: number
    ) => void;
    onNodeDeleted: (nodeId: string) => void;
    onCancel: () => void;
}

type Tone = 'primary' | 'warning' | 'info';

const toneBackgrounds: Record<Tone, string> = {
    primary: 'bg-primary/10',
    warning: 'bg-warning/10',
    info: 'bg-info/10'
};

const FormSection: React.FC<{
    title?: string;
    children: React.ReactNode;
}> = ({ title, children }) => (
    <div className="mb-5 flex flex-col gap-2">
        {title && (
            <span className="text-[10px] font-semibold tracking-wider text-muted-foreground uppercase">
                {title}
            </span>
        )}
        {children}
    </div>
);

const FormHeader: React.FC<{
    title: string;
    subtitle?: string;
    icon: React.ReactNode;
    tone: Tone;
    badge?: React.ReactNode;
    onDelete?: () => void;
    deleteDisabled?: boolean;
    deleteTooltip?: string;
}> = ({
    title,
    subtitle,
    icon,
    tone,
    badge,
    onDelete,
    deleteDisabled,
    deleteTooltip
}) => (
    <div className="mb-6 flex items-start justify-between gap-4">
        <div className="flex items-center gap-3">
            <div
                className={cn(
                    'flex size-10 items-center justify-center rounded-lg',
                    toneBackgrounds[tone]
                )}
            >
                {icon}
            </div>
            <div>
                <div className="flex items-center gap-2">
                    <h2 className="text-lg font-semibold">{title}</h2>
                    {badge}
                </div>
                {subtitle && (
                    <span className="text-xs text-muted-foreground">
                        {subtitle}
                    </span>
                )}
            </div>
        </div>
        {onDelete && (
            <Tooltip>
                <TooltipTrigger asChild>
                    <Button
                        variant="ghost"
                        size="icon-sm"
                        className="bg-destructive/10 text-destructive hover:bg-destructive/20"
                        onClick={onDelete}
                        disabled={deleteDisabled}
                    >
                        <Trash2 />
                    </Button>
                </TooltipTrigger>
                <TooltipContent>{deleteTooltip || 'Delete'}</TooltipContent>
            </Tooltip>
        )}
    </div>
);

export const BadgeDetailPanel = observer(
    ({
        selectedNode,
        createMode,
        onNodeUpdated,
        onNodeCreated,
        onNodeDeleted,
        onCancel
    }: BadgeDetailPanelProps) => {
        const isCreate = createMode !== null;
        const nodeType = createMode?.nodeType || selectedNode?.nodeType;
        const parentId = createMode?.parentId || selectedNode?.parentId;

        if (!selectedNode && !createMode) {
            return (
                <div className="flex h-full flex-col items-center justify-center gap-4 p-8">
                    <div className="flex size-18 items-center justify-center rounded-lg bg-muted">
                        <Trophy className="size-9 text-muted-foreground" />
                    </div>
                    <div className="flex flex-col gap-1 text-center">
                        <p className="text-sm font-medium text-muted-foreground">
                            No item selected
                        </p>
                        <p className="text-xs text-muted-foreground">
                            Select a category, type, or level from the tree to
                            view and edit details
                        </p>
                    </div>
                </div>
            );
        }

        return (
            <div className="h-full overflow-auto p-4">
                {nodeType === 'category' && (
                    <CategoryForm
                        category={
                            isCreate
                                ? null
                                : (selectedNode?.data as BadgeCategoryAdminDto)
                        }
                        nodeId={selectedNode?.id}
                        onNodeUpdated={onNodeUpdated}
                        onNodeCreated={(data) =>
                            onNodeCreated('category', data)
                        }
                        onNodeDeleted={onNodeDeleted}
                        onCancel={onCancel}
                    />
                )}
                {nodeType === 'type' && (
                    <TypeForm
                        badgeType={
                            isCreate
                                ? null
                                : (selectedNode?.data as BadgeTypeAdminDto)
                        }
                        nodeId={selectedNode?.id}
                        parentCategoryId={parentId}
                        onNodeUpdated={onNodeUpdated}
                        onNodeCreated={(data, catId) =>
                            onNodeCreated('type', data, catId)
                        }
                        onNodeDeleted={onNodeDeleted}
                        onCancel={onCancel}
                    />
                )}
                {nodeType === 'level' && (
                    <LevelForm
                        badgeLevel={
                            isCreate
                                ? null
                                : (selectedNode?.data as BadgeLevelAdminDto)
                        }
                        nodeId={selectedNode?.id}
                        parentTypeId={parentId}
                        onNodeUpdated={onNodeUpdated}
                        onNodeCreated={(data, typeId) =>
                            onNodeCreated('level', data, typeId)
                        }
                        onNodeDeleted={onNodeDeleted}
                        onCancel={onCancel}
                    />
                )}
            </div>
        );
    }
);

interface CategoryFormProps {
    category: BadgeCategoryAdminDto | null;
    nodeId?: string;
    onNodeUpdated: (nodeId: string, data: BadgeCategoryAdminDto) => void;
    onNodeCreated: (data: BadgeCategoryAdminDto) => void;
    onNodeDeleted: (nodeId: string) => void;
    onCancel: () => void;
}

const CategoryForm = observer(
    ({
        category,
        nodeId,
        onNodeUpdated,
        onNodeCreated,
        onNodeDeleted,
        onCancel
    }: CategoryFormProps) => {
        const { fitnessActivityClient } = injectTSDI(Api);
        const isEdit = category !== null;
        const [isSubmitting, setIsSubmitting] = React.useState(false);

        const [key, setKey] = React.useState('');
        const [name, setName] = React.useState<I18NText>({ en: '' });
        const [description, setDescription] = React.useState<I18NText>({
            en: ''
        });
        const [badgeKind, setBadgeKind] = React.useState<BadgeKind>(
            BadgeKind.STANDARD
        );
        const [sortOrder, setSortOrder] = React.useState(0);
        const [enabled, setEnabled] = React.useState(true);

        React.useEffect(() => {
            if (category) {
                setKey(category.key || '');
                setName(category.name || { en: '' });
                setDescription(category.description || { en: '' });
                setBadgeKind(category.badgeKind);
                setSortOrder(category.sortOrder);
                setEnabled(category.enabled ?? true);
            } else {
                setKey('');
                setName({ en: '' });
                setDescription({ en: '' });
                setBadgeKind(BadgeKind.STANDARD);
                setSortOrder(0);
                setEnabled(true);
            }
        }, [category]);

        const handleSubmit = async () => {
            setIsSubmitting(true);
            try {
                if (isEdit && category && nodeId) {
                    const response =
                        await fitnessActivityClient.admin.badgeCategoryAdminControllerUpdateCategory(
                            category.id,
                            {
                                name: name as Record<string, string>,
                                description: Object.values(description).some(
                                    (v) => v
                                )
                                    ? (description as Record<string, string>)
                                    : undefined,
                                badgeKind,
                                sortOrder,
                                enabled
                            }
                        );
                    toast.success('Saved');
                    onNodeUpdated(nodeId, response.data);
                } else {
                    const response =
                        await fitnessActivityClient.admin.badgeCategoryAdminControllerCreateCategory(
                            {
                                key:
                                    key ||
                                    name.en
                                        ?.toLowerCase()
                                        .replace(/\s+/g, '_') ||
                                    `category_${Date.now()}`,
                                name: name as Record<string, string>,
                                description: Object.values(description).some(
                                    (v) => v
                                )
                                    ? (description as Record<string, string>)
                                    : undefined,
                                badgeKind,
                                sortOrder,
                                enabled
                            }
                        );
                    toast.success('Created');
                    onNodeCreated(response.data);
                }
            } catch (error) {
                toast.error('Failed to save');
            } finally {
                setIsSubmitting(false);
            }
        };

        const handleDelete = async () => {
            if (
                !category ||
                !nodeId ||
                !window.confirm('Delete this category?')
            )
                return;
            setIsSubmitting(true);
            try {
                await fitnessActivityClient.admin.badgeCategoryAdminControllerDeleteCategory(
                    category.id
                );
                toast.success('Deleted');
                onNodeDeleted(nodeId);
            } catch (error) {
                toast.error('Failed to delete');
            } finally {
                setIsSubmitting(false);
            }
        };

        const categoryName = name['en'] || Object.values(name)[0] || '';
        const headerTitle = isEdit
            ? categoryName || 'Category'
            : 'New Category';

        return (
            <div className="max-w-lg">
                <FormHeader
                    title={headerTitle}
                    subtitle={
                        isEdit
                            ? 'Badge Category'
                            : 'Create a new badge category'
                    }
                    icon={<Shapes className="size-5 text-primary" />}
                    tone="primary"
                    badge={
                        category?.deleted ? (
                            <Badge variant="warning">Archived</Badge>
                        ) : !enabled ? (
                            <Badge variant="muted">Disabled</Badge>
                        ) : undefined
                    }
                    onDelete={isEdit ? handleDelete : undefined}
                    deleteDisabled={isSubmitting}
                    deleteTooltip="Delete category"
                />

                <FormSection title="Basic Information">
                    <LocalizedTextField
                        label="Name"
                        type="text"
                        value={name}
                        onChange={setName}
                        required
                    />
                    <LocalizedTextArea
                        label="Description"
                        value={description}
                        onChange={setDescription}
                        resetKey={category?.id ?? 0}
                    />
                </FormSection>

                <Separator className="my-6" />

                <FormSection title="Settings">
                    <Select
                        label="Kind"
                        value={badgeKind}
                        options={Object.values(BadgeKind).map((k) => ({
                            value: k,
                            label: k.charAt(0) + k.slice(1).toLowerCase()
                        }))}
                        onChange={(v) => setBadgeKind(v as BadgeKind)}
                    />
                </FormSection>

                <Separator className="my-6" />

                <FormSection title="Status">
                    <div className="flex items-center gap-3">
                        <Switch
                            id="category-enabled"
                            checked={enabled}
                            onCheckedChange={setEnabled}
                        />
                        <Label
                            htmlFor="category-enabled"
                            className="flex flex-col items-start gap-0.5"
                        >
                            <span className="text-sm font-medium">
                                {enabled ? 'Enabled' : 'Disabled'}
                            </span>
                            <span className="text-xs text-muted-foreground">
                                {enabled
                                    ? 'Category is visible to users'
                                    : 'Category is hidden from users'}
                            </span>
                        </Label>
                    </div>
                </FormSection>

                <Separator className="my-6" />

                <div className="flex justify-end gap-3">
                    <Button
                        variant="ghost"
                        onClick={onCancel}
                        disabled={isSubmitting}
                    >
                        Cancel
                    </Button>
                    <Button
                        onClick={handleSubmit}
                        disabled={isSubmitting || !name.en}
                        loading={isSubmitting}
                    >
                        {isEdit ? <Save /> : <Plus />}
                        {isEdit ? 'Save Changes' : 'Create Category'}
                    </Button>
                </div>
            </div>
        );
    }
);

interface TypeFormProps {
    badgeType: BadgeTypeAdminDto | null;
    nodeId?: string;
    parentCategoryId?: number;
    onNodeUpdated: (nodeId: string, data: BadgeTypeAdminDto) => void;
    onNodeCreated: (data: BadgeTypeAdminDto, categoryId: number) => void;
    onNodeDeleted: (nodeId: string) => void;
    onCancel: () => void;
}

const TypeForm = observer(
    ({
        badgeType,
        nodeId,
        parentCategoryId,
        onNodeUpdated,
        onNodeCreated,
        onNodeDeleted,
        onCancel
    }: TypeFormProps) => {
        const { fitnessActivityClient } = injectTSDI(Api);
        const isEdit = badgeType !== null;
        const [isSubmitting, setIsSubmitting] = React.useState(false);

        const [categoryId, setCategoryId] = React.useState<number | ''>(
            parentCategoryId || ''
        );
        const [key, setKey] = React.useState('');
        const [name, setName] = React.useState<I18NText>({ en: '' });
        const [description, setDescription] = React.useState<I18NText>({
            en: ''
        });
        const [status, setStatus] = React.useState<BadgeStatus>(
            BadgeStatus.DRAFT
        );
        const [visibility, setVisibility] = React.useState<BadgeVisibility>(
            BadgeVisibility.PUBLIC
        );
        const [ownerType, setOwnerType] = React.useState<BadgeOwnerType>(
            BadgeOwnerType.COMMUNITY
        );
        const [ownerCommunityId, setOwnerCommunityId] = React.useState<
            number | undefined
        >(undefined);
        const [goalId, setGoalId] = React.useState<number | undefined>(
            undefined
        );
        const [sortOrder, setSortOrder] = React.useState(0);
        const [categories, setCategories] = React.useState<
            BadgeCategoryAdminDto[]
        >([]);

        const categoriesRequest = useWrapRequest(async () => {
            const result =
                await fitnessActivityClient.admin.badgeCategoryAdminControllerGetCategories(
                    { size: 100 }
                );
            return result.data.content || [];
        });

        React.useEffect(() => {
            categoriesRequest.request([]);
        }, []);
        React.useEffect(() => {
            if (categoriesRequest.result)
                setCategories(categoriesRequest.result);
        }, [categoriesRequest.result]);

        React.useEffect(() => {
            if (badgeType) {
                setCategoryId(badgeType.categoryId);
                setKey(badgeType.key || '');
                setName(badgeType.name || { en: '' });
                setDescription(badgeType.description || { en: '' });
                setStatus(badgeType.status);
                setVisibility(badgeType.visibility);
                setOwnerType(badgeType.ownerType);
                setOwnerCommunityId(badgeType.ownerCommunityId ?? undefined);
                setGoalId(badgeType.goalId ?? undefined);
                setSortOrder(badgeType.sortOrder);
            } else {
                setCategoryId(parentCategoryId || '');
                setKey('');
                setName({ en: '' });
                setDescription({ en: '' });
                setStatus(BadgeStatus.DRAFT);
                setVisibility(BadgeVisibility.PUBLIC);
                setOwnerType(BadgeOwnerType.COMMUNITY);
                setOwnerCommunityId(undefined);
                setGoalId(undefined);
                setSortOrder(0);
            }
        }, [badgeType, parentCategoryId]);

        const handleSubmit = async () => {
            if (!categoryId) {
                toast.error('Select a category');
                return;
            }
            setIsSubmitting(true);
            try {
                if (isEdit && badgeType && nodeId) {
                    const goalChanged =
                        goalId !== (badgeType.goalId ?? undefined);
                    const response =
                        await fitnessActivityClient.admin.badgeTypeAdminControllerUpdateType(
                            badgeType.id,
                            {
                                categoryId,
                                name: name as Record<string, string>,
                                description: Object.values(description).some(
                                    (v) => v
                                )
                                    ? (description as Record<string, string>)
                                    : undefined,
                                status,
                                visibility,
                                ownerType,
                                ownerCommunityId:
                                    ownerType === BadgeOwnerType.COMMUNITY
                                        ? ownerCommunityId
                                        : undefined,
                                sortOrder,
                                goalId: goalChanged ? goalId : undefined,
                                unlinkGoal:
                                    goalChanged && goalId === undefined
                                        ? true
                                        : undefined
                            }
                        );
                    toast.success('Saved');
                    onNodeUpdated(nodeId, response.data);
                } else {
                    const response =
                        await fitnessActivityClient.admin.badgeTypeAdminControllerCreateType(
                            {
                                key:
                                    key ||
                                    name.en
                                        ?.toLowerCase()
                                        .replace(/\s+/g, '_') ||
                                    `type_${Date.now()}`,
                                categoryId,
                                name: name as Record<string, string>,
                                description: Object.values(description).some(
                                    (v) => v
                                )
                                    ? (description as Record<string, string>)
                                    : undefined,
                                status,
                                visibility,
                                ownerType,
                                ownerCommunityId:
                                    ownerType === BadgeOwnerType.COMMUNITY
                                        ? ownerCommunityId
                                        : undefined,
                                sortOrder,
                                goalId
                            }
                        );
                    toast.success('Created');
                    onNodeCreated(response.data, categoryId);
                }
            } catch (error) {
                toast.error('Failed to save');
            } finally {
                setIsSubmitting(false);
            }
        };

        const handleDelete = async () => {
            if (
                !badgeType ||
                !nodeId ||
                !window.confirm('Delete this badge type?')
            )
                return;
            setIsSubmitting(true);
            try {
                await fitnessActivityClient.admin.badgeTypeAdminControllerDeleteType(
                    badgeType.id
                );
                toast.success('Deleted');
                onNodeDeleted(nodeId);
            } catch (error) {
                toast.error('Failed to delete');
            } finally {
                setIsSubmitting(false);
            }
        };

        const isEarned =
            badgeType?.earnedByUsersCount && badgeType.earnedByUsersCount > 0;
        const typeName = name['en'] || Object.values(name)[0] || '';
        const headerTitle = isEdit
            ? typeName || 'Badge Type'
            : 'New Badge Type';

        return (
            <div>
                <FormHeader
                    title={headerTitle}
                    subtitle={isEdit ? 'Badge Type' : 'Create a new badge type'}
                    icon={<Trophy className="size-5 text-warning" />}
                    tone="warning"
                    badge={
                        isEarned ? (
                            <Badge variant="success">
                                {`${badgeType?.earnedByUsersCount} earned`}
                            </Badge>
                        ) : undefined
                    }
                    onDelete={isEdit ? handleDelete : undefined}
                    deleteDisabled={isSubmitting}
                    deleteTooltip="Delete badge type"
                />

                {isEarned && (
                    <Alert variant="info" className="mb-6">
                        <Info />
                        <AlertDescription>
                            Changes may affect users who earned this badge.
                        </AlertDescription>
                    </Alert>
                )}

                <div className="grid gap-6 md:grid-cols-12">
                    <div className="md:col-span-7">
                        <FormSection title="Basic Information">
                            <LocalizedTextField
                                label="Name"
                                type="text"
                                value={name}
                                onChange={setName}
                                required
                            />
                            <Select
                                label="Category"
                                value={String(categoryId)}
                                options={categories.map((c) => ({
                                    value: String(c.id),
                                    label:
                                        c.name['en'] || Object.values(c.name)[0]
                                }))}
                                onChange={(v) => setCategoryId(Number(v))}
                            />
                            <LocalizedTextArea
                                label="Description"
                                value={description}
                                onChange={setDescription}
                                resetKey={badgeType?.id ?? 0}
                            />
                        </FormSection>
                    </div>

                    <div className="md:col-span-5">
                        <FormSection title="Status & Visibility">
                            <Select
                                label="Status"
                                value={status}
                                options={Object.values(BadgeStatus).map(
                                    (s) => ({
                                        value: s,
                                        label:
                                            s.charAt(0) +
                                            s.slice(1).toLowerCase()
                                    })
                                )}
                                onChange={(v) => setStatus(v as BadgeStatus)}
                            />
                            <Select
                                label="Visibility"
                                value={visibility}
                                options={Object.values(BadgeVisibility).map(
                                    (v) => ({
                                        value: v,
                                        label:
                                            v.replace('_', ' ').charAt(0) +
                                            v
                                                .replace('_', ' ')
                                                .slice(1)
                                                .toLowerCase()
                                    })
                                )}
                                onChange={(v) =>
                                    setVisibility(v as BadgeVisibility)
                                }
                            />
                            <Select
                                label="Owner"
                                value={ownerType}
                                options={Object.values(BadgeOwnerType).map(
                                    (o) => ({
                                        value: o,
                                        label:
                                            o.charAt(0) +
                                            o.slice(1).toLowerCase()
                                    })
                                )}
                                onChange={(v) => {
                                    const next = v as BadgeOwnerType;
                                    setOwnerType(next);
                                    if (next !== BadgeOwnerType.COMMUNITY)
                                        setOwnerCommunityId(undefined);
                                }}
                            />
                            {ownerType === BadgeOwnerType.COMMUNITY && (
                                <CommunitySelect
                                    value={ownerCommunityId}
                                    onChange={setOwnerCommunityId}
                                    helperText="Leave empty to make this badge available to all communities."
                                />
                            )}
                        </FormSection>

                        <Separator className="my-6" />

                        <FormSection title="Linked Goal">
                            <GoalSelect
                                value={goalId}
                                onChange={setGoalId}
                                communityId={
                                    ownerType === BadgeOwnerType.COMMUNITY
                                        ? ownerCommunityId
                                        : undefined
                                }
                                helperText="Link an existing goal to show progress toward this badge. Goals are defined in Community."
                            />
                        </FormSection>
                    </div>
                </div>

                <Separator className="my-6" />

                <div className="flex justify-end gap-3">
                    <Button
                        variant="ghost"
                        onClick={onCancel}
                        disabled={isSubmitting}
                    >
                        Cancel
                    </Button>
                    <Button
                        onClick={handleSubmit}
                        disabled={isSubmitting || !name.en || !categoryId}
                        loading={isSubmitting}
                    >
                        {isEdit ? <Save /> : <Plus />}
                        {isEdit ? 'Save Changes' : 'Create Type'}
                    </Button>
                </div>
            </div>
        );
    }
);

interface LevelFormProps {
    badgeLevel: BadgeLevelAdminDto | null;
    nodeId?: string;
    parentTypeId?: number;
    onNodeUpdated: (nodeId: string, data: BadgeLevelAdminDto) => void;
    onNodeCreated: (data: BadgeLevelAdminDto, typeId: number) => void;
    onNodeDeleted: (nodeId: string) => void;
    onCancel: () => void;
}

const LevelForm = observer(
    ({
        badgeLevel,
        nodeId,
        parentTypeId,
        onNodeUpdated,
        onNodeCreated,
        onNodeDeleted,
        onCancel
    }: LevelFormProps) => {
        const { fitnessActivityClient } = injectTSDI(Api);
        const isEdit = badgeLevel !== null;
        const [isSubmitting, setIsSubmitting] = React.useState(false);

        const [badgeTypeId, setBadgeTypeId] = React.useState<number | ''>(
            parentTypeId || ''
        );
        const [levelIndex, setLevelIndex] = React.useState(1);
        const [name, setName] = React.useState<I18NText>({ en: '' });
        const [description, setDescription] = React.useState<I18NText>({
            en: ''
        });
        const [threshold, setThreshold] = React.useState(0);
        const [image, setImage] = React.useState<CdnObjectDto | null>(null);
        const [badgeTypes, setBadgeTypes] = React.useState<BadgeTypeAdminDto[]>(
            []
        );

        const typesRequest = useWrapRequest(async () => {
            const result =
                await fitnessActivityClient.admin.badgeTypeAdminControllerGetTypes(
                    { size: 100 }
                );
            return result.data.content || [];
        });

        React.useEffect(() => {
            typesRequest.request([]);
        }, []);
        React.useEffect(() => {
            if (typesRequest.result) setBadgeTypes(typesRequest.result);
        }, [typesRequest.result]);

        React.useEffect(() => {
            if (badgeLevel) {
                setBadgeTypeId(badgeLevel.badgeTypeId);
                setLevelIndex(badgeLevel.levelIndex);
                setName(badgeLevel.name || { en: '' });
                setDescription(badgeLevel.description || { en: '' });
                setThreshold(badgeLevel.threshold);
                setImage(badgeLevel.image || null);
            } else {
                setBadgeTypeId(parentTypeId || '');
                setName({ en: '' });
                setDescription({ en: '' });
                setThreshold(0);
                setImage(null);

                if (parentTypeId) {
                    fitnessActivityClient.admin
                        .badgeLevelAdminControllerGetLevels({
                            badgeTypeId: parentTypeId,
                            size: 1,
                            sort: ['levelIndex,DESC']
                        })
                        .then((res) => {
                            const maxIndex =
                                res?.data?.content?.[0]?.levelIndex ?? 0;
                            setLevelIndex(maxIndex + 1);
                        })
                        .catch(() => setLevelIndex(1));
                } else {
                    setLevelIndex(1);
                }
            }
        }, [badgeLevel, parentTypeId]);

        const handleSubmit = async () => {
            if (!badgeTypeId) {
                toast.error('Select a badge type');
                return;
            }
            setIsSubmitting(true);
            try {
                if (isEdit && badgeLevel && nodeId) {
                    const response =
                        await fitnessActivityClient.admin.badgeLevelAdminControllerUpdateLevel(
                            badgeLevel.id,
                            {
                                levelIndex,
                                name: name as Record<string, string>,
                                description: Object.values(description).some(
                                    (v) => v
                                )
                                    ? (description as Record<string, string>)
                                    : undefined,
                                image: image || undefined,
                                threshold
                            }
                        );
                    toast.success('Saved');
                    onNodeUpdated(nodeId, response.data);
                } else {
                    const response =
                        await fitnessActivityClient.admin.badgeLevelAdminControllerCreateLevel(
                            {
                                badgeTypeId,
                                levelIndex,
                                name: name as Record<string, string>,
                                description: Object.values(description).some(
                                    (v) => v
                                )
                                    ? (description as Record<string, string>)
                                    : undefined,
                                image: image || undefined,
                                threshold
                            }
                        );
                    toast.success('Created');
                    onNodeCreated(response.data, badgeTypeId);
                }
            } catch (error: any) {
                toast.error(error?.error?.message || 'Failed to save');
            } finally {
                setIsSubmitting(false);
            }
        };

        const handleDelete = async () => {
            if (!badgeLevel || !nodeId || !window.confirm('Delete this level?'))
                return;
            setIsSubmitting(true);
            try {
                await fitnessActivityClient.admin.badgeLevelAdminControllerDeleteLevel(
                    badgeLevel.id
                );
                toast.success('Deleted');
                onNodeDeleted(nodeId);
            } catch (error) {
                toast.error('Cannot delete - level may have been earned');
            } finally {
                setIsSubmitting(false);
            }
        };

        const isEarned =
            badgeLevel?.earnedByUsersCount && badgeLevel.earnedByUsersCount > 0;

        const levelName = name['en'] || Object.values(name)[0] || '';
        const headerTitle = isEdit
            ? levelName || 'Badge Level'
            : 'New Badge Level';

        return (
            <div>
                <FormHeader
                    title={headerTitle}
                    subtitle={
                        isEdit
                            ? `Level ${levelIndex}`
                            : 'Create a new badge level'
                    }
                    icon={<Award className="size-5 text-info" />}
                    tone="info"
                    badge={
                        isEarned ? (
                            <Badge variant="success">
                                {`${badgeLevel?.earnedByUsersCount} earned`}
                            </Badge>
                        ) : undefined
                    }
                    onDelete={isEdit && !isEarned ? handleDelete : undefined}
                    deleteDisabled={isSubmitting}
                    deleteTooltip={
                        isEarned
                            ? 'Cannot delete - level has been earned'
                            : 'Delete level'
                    }
                />

                {isEarned && (
                    <Alert variant="warning" className="mb-6">
                        <Info />
                        <AlertDescription>
                            This level has been earned by users and cannot be
                            deleted.
                        </AlertDescription>
                    </Alert>
                )}

                <div className="grid gap-6 md:grid-cols-12">
                    <div className="md:col-span-7">
                        <FormSection title="Basic Information">
                            <LocalizedTextField
                                label="Name"
                                type="text"
                                value={name}
                                onChange={setName}
                                required
                            />

                            <div className="grid grid-cols-3 gap-4">
                                <div className="flex flex-col rounded-md border bg-muted/50 p-3">
                                    <span className="text-xs text-muted-foreground">
                                        Level
                                    </span>
                                    <span className="font-medium">
                                        Level {levelIndex}
                                    </span>
                                </div>
                                <div className="col-span-2">
                                    <TextField
                                        label="Threshold"
                                        type="number"
                                        value={threshold}
                                        onChange={(val) =>
                                            setThreshold(
                                                parseFloat(String(val)) || 0
                                            )
                                        }
                                        tooltipText="Value required to earn this level"
                                    />
                                </div>
                            </div>

                            {!isEdit && (
                                <Select
                                    label="Badge Type"
                                    value={String(badgeTypeId)}
                                    options={badgeTypes.map((t) => ({
                                        value: String(t.id),
                                        label:
                                            t.name['en'] ||
                                            Object.values(t.name)[0]
                                    }))}
                                    onChange={(v) => setBadgeTypeId(Number(v))}
                                />
                            )}
                        </FormSection>

                        <Separator className="my-6" />

                        <FormSection title="Description">
                            <LocalizedTextArea
                                label="Description"
                                value={description}
                                onChange={setDescription}
                                resetKey={badgeLevel?.id ?? 0}
                            />
                        </FormSection>
                    </div>

                    <div className="md:col-span-5">
                        <FormSection title="Badge Image">
                            <div className="min-h-50 rounded-lg border bg-background/50 p-4">
                                <BadgeImageUpload
                                    label=""
                                    helperText="Upload an image that represents this badge level"
                                    initialImage={image}
                                    onUploaded={(img) => setImage(img)}
                                    onRemoved={() => setImage(null)}
                                />
                            </div>
                        </FormSection>
                    </div>
                </div>

                <Separator className="my-6" />

                <div className="flex justify-end gap-3">
                    <Button
                        variant="ghost"
                        onClick={onCancel}
                        disabled={isSubmitting}
                    >
                        Cancel
                    </Button>
                    <Button
                        onClick={handleSubmit}
                        disabled={isSubmitting || !name.en || !badgeTypeId}
                        loading={isSubmitting}
                    >
                        {isEdit ? <Save /> : <Plus />}
                        {isEdit ? 'Save Changes' : 'Create Level'}
                    </Button>
                </div>
            </div>
        );
    }
);

export default BadgeDetailPanel;
